<template>
  <section class="mb-3" :aria-label="`${label} importieren`" :data-testid="`${kind}-import-panel`">
    <p class="text-muted small mb-2">Die Vorschau speichert keine Datensätze. Fehler und bestehende Einträge müssen vor der vollständigen Übernahme geprüft werden.</p>
    <p v-if="kind === 'company' && !userStore.isSystemAdmin" class="small text-muted">Der Firmenimport kann nur Ihre aktive Firma „{{ userStore.currentCompany?.name || userStore.currentCompanyId }}“ aktualisieren. Neue Firmen können Systemadministratoren anlegen.</p>
    <label :for="`${kind}ImportFile`" class="form-label">Importdatei ({{ allowCsv ? 'JSON oder CSV' : 'JSON' }})</label>
    <input :id="`${kind}ImportFile`" ref="fileInput" type="file" class="form-control" :accept="allowCsv ? '.json,.csv' : '.json'" :disabled="busy" @change="selectFile" />
    <div class="d-flex gap-2 mt-2">
      <button class="btn btn-outline-primary" :disabled="busy || !content" @click="previewImport">Vorschau prüfen</button>
      <button class="btn btn-primary" :disabled="!canApply" @click="applyImport">Geprüften Import übernehmen</button>
    </div>
    <div v-if="error" class="alert alert-danger mt-3" role="alert">{{ error }}</div>
    <div v-if="result" class="alert alert-success mt-3" role="status">Import abgeschlossen: {{ result.created }} angelegt, {{ result.updated }} aktualisiert, {{ result.skipped }} übersprungen.</div>
    <div v-if="preview" class="mt-3">
      <div v-if="reviewedRevision !== revision" class="alert alert-warning">Die Auswahl wurde geändert. Bitte die Vorschau erneut prüfen.</div>
      <ul v-if="preview.errors?.length" class="text-danger"><li v-for="(item, index) in preview.errors" :key="index">{{ item }}</li></ul>
      <p>{{ preview.created }} neu, {{ preview.updated }} aktualisieren, {{ preview.skipped }} überspringen.</p>
      <p v-if="kind === 'company'" class="small text-muted">Enthaltene Steuerkennungen und Zahlungsbedingungen ersetzen die bisherigen Listen der aktualisierten Firma. Für Zahlungsbedingungen sind Schreib- und Löschrechte sowie bei betroffenem Standard das Recht zur Standardänderung erforderlich. Tarifpläne werden beibehalten.</p>
      <div class="table-responsive">
        <table class="table table-sm">
          <thead><tr><th>Datensatz</th><th>Nummer / Name</th><th>Entscheidung</th><th>Prüfung</th></tr></thead>
          <tbody><tr v-for="row in preview.rows" :key="row.row">
            <td>{{ row.row }}</td><td>{{ row.number || '–' }} / {{ row.name || '–' }}</td>
            <td><select v-if="row.conflict" class="form-select form-select-sm" :value="decisions[row.row] || ''" :aria-label="`Konflikt in Datensatz ${row.row}`" :disabled="busy" @change="choose(row.row, $event.target.value)">
              <option value="">Bitte wählen</option><option value="update">Aktualisieren</option><option value="skip">Überspringen</option>
            </select><span v-else>Neu anlegen</span></td>
            <td><ul v-if="row.errors?.length" class="text-danger mb-0"><li v-for="(item, index) in row.errors" :key="index">{{ item }}</li></ul><span v-else>Geprüft</span></td>
          </tr></tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script>
import MasterDataImportService from "@/services/MasterDataImportService";
import { useUserStore } from "@/stores/userStore";
import { importFormat, importRequest, canApplyPreview } from "@/utils/importReview.cjs";

export default {
  name: "MasterDataImportPanel",
  props: { kind: { type: String, required: true }, label: { type: String, required: true }, allowCsv: { type: Boolean, default: true } },
  emits: ["imported"],
  data: () => ({ content: "", format: "json", decisions: {}, preview: null, result: null, error: "", busy: false, revision: 0, reviewedRevision: -1, userStore: useUserStore() }),
  computed: {
    scope() { return `${this.userStore.userId}/${this.userStore.currentCompanyId}`; },
    canApply() { return canApplyPreview(this.preview, this.reviewedRevision, this.revision, this.busy); },
  },
  watch: { scope() { this.reset(); } },
  beforeUnmount() { this.revision++; },
  methods: {
    reset() {
      this.revision++; this.content = ""; this.decisions = {}; this.preview = null; this.result = null; this.error = "";
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    async selectFile(event) {
      const file = event.target.files?.[0];
      this.revision++; this.content = ""; this.decisions = {}; this.preview = null; this.result = null; this.error = "";
      if (!file) return;
      const revision = this.revision;
      try {
        if (file.size > 8000000) throw new Error("Die Datei ist zu groß (höchstens 2 Millionen Zeichen).");
        const format = importFormat(file.name, this.allowCsv);
        const content = await file.text();
        if (content.length > 2000000) throw new Error("Die Datei darf höchstens 2 Millionen Zeichen enthalten.");
        if (revision === this.revision) { this.content = content; this.format = format; }
      } catch (error) { if (revision === this.revision) this.error = error.message; }
    },
    choose(row, action) { this.decisions = { ...this.decisions, [row]: action }; this.revision++; this.result = null; },
    async previewImport() {
      if (this.busy || !this.content) return;
      this.busy = true; this.error = ""; this.result = null;
      const revision = this.revision;
      try {
        const response = await MasterDataImportService.preview(this.kind, importRequest(this.content, this.format, this.decisions));
        if (revision === this.revision) { this.preview = response.data; this.reviewedRevision = revision; }
      } catch (error) { if (revision === this.revision) this.error = error.response?.data?.detail || "Die Vorschau konnte nicht erstellt werden."; }
      finally { this.busy = false; }
    },
    async applyImport() {
      if (!this.canApply) return;
      const revision = this.revision;
      const input = { ...importRequest(this.content, this.format, this.decisions), previewToken: this.preview.previewToken };
      this.busy = true; this.error = "";
      try {
        const response = await MasterDataImportService.apply(this.kind, input);
        if (revision !== this.revision) return;
        this.reset(); this.result = response.data; this.$emit("imported");
      } catch (error) {
        if (revision === this.revision) { this.reviewedRevision = -1; this.error = error.response?.data?.detail || "Der Import wurde nicht übernommen. Bitte erneut prüfen."; }
      } finally { this.busy = false; }
    },
  },
};
</script>
